/**
 * Local Timer Designs catalog — 10 hand-crafted aesthetic timer-card designs
 * for the Timer widget "design store" (mirrors the Alerts store flow).
 *
 * Source: the Claude Design pack
 * "eriol-overlay-variants-intermission/project/Timer Widgets.html" — 10 full
 * 1920×1080 SVG <template> scenes whose timer card lives bottom-left in
 * `<g transform="translate(120 740)">` as a ~600×300 card.
 *
 * RENDER MODEL (fixed user decision): we use ONLY the ~600×300 card of each
 * design (not the full scene). Each `frame` below is the verbatim inner content
 * of that design's `<g transform="translate(120 740)">` (the translate is
 * DROPPED so the card sits at 0,0), PLUS the template's `<defs>`, re-wrapped in
 * an `<svg viewBox="0 0 cardWidth cardHeight">` sized to that design's card.
 *
 * PURE-DATA format: every design is a {@link SerializableDesign} (plain data,
 * no JS), so it can be stored / transmitted / PUBLISHED safely. The generic
 * {@link renderDesign} turns the data + live values into the final SVG.
 *
 * Pixel-perfect rule: the SVG IS the design — the markup is reused verbatim
 * EXCEPT for two mechanical transforms:
 *   1. `<defs>` ids (gradients / patterns / filters like `td-dots`, `tf-tile`,
 *      `nt-glow`, `ta-rgb`, …) collide across designs when several cards are
 *      inlined on one page (the gallery). Every defs id is PREFIXED with the
 *      design id (e.g. `td-dots` → `dark-td-dots`) and every `url(#…)` /
 *      `href="#…"` / `filter="url(#…)"` reference is updated to match.
 *   2. The big time text `05:00` is the placeholder `{{TIME}}`, backed by a
 *      `time` slot. For `flip` (split-flap tiles "05" and "00") the two tiles
 *      use `{{MM}}` and `{{SS}}`. `renderTimerDesignSvg` binds these as live
 *      values at render time.
 *
 * fonts = the Google Fonts that design's SVG references (so the live Pixi
 * rasteriser / DOM preview can ensure they are loaded before drawing).
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type { SerializableDesign } from "../entities/designs/serializableDesign";

export type TimerDesignCategory = "countdown" | "clock";

/** A timer-store design: a {@link SerializableDesign} narrowed to `widgetType:"timer"`. */
export interface TimerDesign extends SerializableDesign {
  widgetType: "timer";
  category: TimerDesignCategory;
}

// ---------------------------------------------------------------------------
// 01 — Dark Minimal (countdown)  ·  card 600×300
// defs: td-dots, td-bar
// ---------------------------------------------------------------------------

const darkFrame = `<svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="dark-td-dots" width="22" height="22" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.1" fill="#33333d" opacity=".6"/></pattern>
    <linearGradient id="dark-td-bar" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#2cd86a"/><stop offset="100%" stop-color="#5aff8a"/></linearGradient>
  </defs>
    <rect width="600" height="300" rx="22" fill="#0d0d12" stroke="#2a2a33" stroke-width="1.5"/>
    <rect width="600" height="300" rx="22" fill="url(#dark-td-dots)"/>
    <text x="48" y="86" font-family="Inter, sans-serif" font-weight="700" font-size="22" fill="#cfd6e2" letter-spacing="8">VOLVEMOS EN</text>
    <text x="552" y="86" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#7fbfff" letter-spacing="4">● PRE-SHOW</text>
    <text x="48" y="200" font-family="DM Mono, monospace" font-weight="500" font-size="130" fill="#f5f5f5" letter-spacing="2">{{TIME}}</text>
    <rect x="48" y="232" width="504" height="14" rx="7" fill="#26262f"/>
    <rect x="48" y="232" width="360" height="14" rx="7" fill="url(#dark-td-bar)"/>
    <text x="48" y="278" font-family="Inter, sans-serif" font-weight="500" font-size="16" fill="#8a8f9c">Estamos preparando todo · gracias por esperar</text>
</svg>`;

// ---------------------------------------------------------------------------
// 02 — Flip Clock (countdown)  ·  card 620×300
// defs: tf-tile  ·  split tiles → {{MM}} / {{SS}}
// ---------------------------------------------------------------------------

const flipFrame = `<svg viewBox="0 0 620 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="flip-tf-tile" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#33333d"/><stop offset="49%" stop-color="#26262e"/><stop offset="51%" stop-color="#1a1a20"/><stop offset="100%" stop-color="#202028"/></linearGradient>
  </defs>
    <rect width="620" height="300" rx="20" fill="#15151a"/>
    <text x="310" y="56" text-anchor="middle" font-family="DM Mono, monospace" font-size="16" fill="#ffb703" letter-spacing="14">STARTING SOON</text>
    <!-- MM tile -->
    <g transform="translate(60 86)">
      <rect width="200" height="160" rx="14" fill="url(#flip-tf-tile)" stroke="#0a0a0d" stroke-width="2"/>
      <line x1="0" y1="80" x2="200" y2="80" stroke="#0a0a0d" stroke-width="3"/>
      <rect x="6" y="6" width="188" height="70" rx="10" fill="#fff" opacity=".05"/>
      <text x="100" y="124" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="120" fill="#f5f5f5">{{MM}}</text>
      <!-- hinge pins -->
      <circle cx="-2" cy="80" r="6" fill="#0a0a0d"/><circle cx="202" cy="80" r="6" fill="#0a0a0d"/>
    </g>
    <!-- colon -->
    <g fill="#ffb703"><circle cx="310" cy="146" r="10"/><circle cx="310" cy="200" r="10"/></g>
    <!-- SS tile -->
    <g transform="translate(360 86)">
      <rect width="200" height="160" rx="14" fill="url(#flip-tf-tile)" stroke="#0a0a0d" stroke-width="2"/>
      <line x1="0" y1="80" x2="200" y2="80" stroke="#0a0a0d" stroke-width="3"/>
      <rect x="6" y="6" width="188" height="70" rx="10" fill="#fff" opacity=".05"/>
      <text x="100" y="124" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="120" fill="#f5f5f5">{{SS}}</text>
      <circle cx="-2" cy="80" r="6" fill="#0a0a0d"/><circle cx="202" cy="80" r="6" fill="#0a0a0d"/>
    </g>
    <text x="60" y="282" font-family="DM Mono, monospace" font-size="13" fill="#6b6b78" letter-spacing="6">MIN · SEG  ·  @eriol.streams</text>
</svg>`;

// ---------------------------------------------------------------------------
// 03 — Y2K Aqua Gel (countdown)  ·  card 600×300
// defs: ty-bg  ·  note: the ring reads "5:00" (no leading zero) in the source;
//   kept verbatim and uses {{TIME}} (the substitution is a literal swap).
// ---------------------------------------------------------------------------

const y2kFrame = `<svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-ty-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="55%" stop-color="#1c8fff"/><stop offset="100%" stop-color="#003a7a"/></linearGradient>
  </defs>
    <rect width="600" height="300" rx="40" fill="url(#y2k-ty-bg)" stroke="#fff" stroke-width="3"/>
    <ellipse cx="300" cy="32" rx="260" ry="14" fill="#fff" opacity=".7"/>
    <g transform="translate(40 40)"><circle r="10" fill="#003a7a"/><circle r="6" fill="#5aff8a"/></g>
    <text x="62" y="46" font-family="Audiowide, sans-serif" font-size="16" fill="#cffff4" letter-spacing="8">LIVE IN…</text>
    <!-- progress ring -->
    <g transform="translate(150 175)">
      <circle r="96" fill="none" stroke="rgba(0,30,80,.4)" stroke-width="16"/>
      <circle r="96" fill="none" stroke="#5aff8a" stroke-width="16" stroke-linecap="round" stroke-dasharray="603" stroke-dashoffset="180" transform="rotate(-90)"/>
      <circle r="80" fill="none" stroke="#fff" stroke-width="1.5" opacity=".6"/>
      <text y="20" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="48" fill="#fff">{{TIME}}</text>
    </g>
    <text x="290" y="150" font-family="Audiowide, sans-serif" font-size="34" fill="#fff" letter-spacing="2">¡YA VOLVEMOS!</text>
    <text x="290" y="186" font-family="DM Mono, monospace" font-size="14" fill="#cffff4" letter-spacing="8">CONECTANDO STREAM · 68%</text>
    <text x="290" y="244" font-family="DM Mono, monospace" font-size="13" fill="#cffff4" letter-spacing="6">@eriol.streams · BSAS · GMT-3</text>
</svg>`;

// ---------------------------------------------------------------------------
// 04 — Cyberpunk Neon (countdown)  ·  card 640×300
// defs: tc-g (filter on the group)  ·  time has a ghost-stroke duplicate
// ---------------------------------------------------------------------------

const cyberFrame = `<svg viewBox="0 0 640 300" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="cyber-tc-g"><feGaussianBlur stdDeviation="2.5"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
  <g filter="url(#cyber-tc-g)">
    <polygon points="0,40 40,0 600,0 640,40 640,260 600,300 40,300 0,260" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="14,46 50,14 590,14 626,46 626,254 590,286 50,286 14,254" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <text x="34" y="44" font-family="DM Mono, monospace" font-size="13" fill="#f5d019" letter-spacing="8">▮ T-MINUS · 起動まで</text>
    <text x="606" y="44" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#ff2e88" letter-spacing="6">● STANDBY</text>
    <text x="320" y="170" text-anchor="middle" font-family="Orbitron, sans-serif" font-weight="900" font-size="110" fill="#00f0ff" letter-spacing="2">{{TIME}}</text>
    <text x="320" y="170" text-anchor="middle" font-family="Orbitron, sans-serif" font-weight="900" font-size="110" fill="none" stroke="#ff2e88" stroke-width="1" opacity=".5" letter-spacing="2" transform="translate(3 0)">{{TIME}}</text>
    <!-- segmented depletion -->
    <g transform="translate(34 210)">
      <rect width="572" height="28" fill="none" stroke="#00f0ff" stroke-width="1.5"/>
      <g fill="#00f0ff"><rect x="6" y="6" width="30" height="16"/><rect x="42" y="6" width="30" height="16"/><rect x="78" y="6" width="30" height="16"/><rect x="114" y="6" width="30" height="16"/><rect x="150" y="6" width="30" height="16"/><rect x="186" y="6" width="30" height="16"/><rect x="222" y="6" width="30" height="16"/></g>
      <g fill="#16323a"><rect x="258" y="6" width="30" height="16"/><rect x="294" y="6" width="30" height="16"/><rect x="330" y="6" width="30" height="16"/><rect x="366" y="6" width="30" height="16"/><rect x="402" y="6" width="30" height="16"/><rect x="438" y="6" width="30" height="16"/><rect x="474" y="6" width="30" height="16"/><rect x="510" y="6" width="30" height="16"/></g>
    </g>
    <text x="34" y="278" font-family="DM Mono, monospace" font-size="13" fill="#88e8d8" letter-spacing="8">// BOOTING SCENE · @eriol.streams</text>
  </g>
</svg>`;

// ---------------------------------------------------------------------------
// 05 — Editorial (clock)  ·  card 600×300  ·  no defs
// ---------------------------------------------------------------------------

const editorialFrame = `<svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
    <!-- telegram / esquela slip -->
    <rect width="600" height="300" fill="#f5efe2"/>
    <rect width="600" height="300" fill="none" stroke="#1a1815" stroke-width="2"/>
    <rect x="10" y="10" width="580" height="280" fill="none" stroke="#8a3a2a" stroke-width="1"/>
    <!-- perforation dots top -->
    <g fill="#1a1815"><circle cx="40" cy="0" r="3"/><circle cx="100" cy="0" r="3"/><circle cx="160" cy="0" r="3"/><circle cx="220" cy="0" r="3"/><circle cx="280" cy="0" r="3"/><circle cx="340" cy="0" r="3"/><circle cx="400" cy="0" r="3"/><circle cx="460" cy="0" r="3"/><circle cx="520" cy="0" r="3"/><circle cx="560" cy="0" r="3"/></g>
    <text x="32" y="58" font-family="DM Mono, monospace" font-size="13" fill="#8a3a2a" letter-spacing="8">TELEGRAMA · URGENTE · N° 042</text>
    <line x1="32" y1="72" x2="568" y2="72" stroke="#1a1815" stroke-width="1"/>
    <text x="32" y="120" font-family="Cormorant Garamond, serif" font-style="italic" font-size="28" fill="#1a1815">vuelvo a transmitir en —</text>
    <!-- big folio time with dotted leaders -->
    <text x="32" y="216" font-family="Cormorant Garamond, serif" font-weight="600" font-size="120" fill="#1a1815">{{TIME}}</text>
    <text x="568" y="160" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#8a3a2a" letter-spacing="6">STOP</text>
    <line x1="32" y1="240" x2="568" y2="240" stroke="#1a1815" stroke-width="1" stroke-dasharray="2 6"/>
    <text x="32" y="276" font-family="DM Mono, monospace" font-size="13" fill="#1a1815" letter-spacing="8">NO SE RETIRE · @ERIOL.STREAMS · STOP</text>
    <!-- wax seal -->
    <g transform="translate(520 250)"><circle r="34" fill="#8a3a2a"/><circle r="26" fill="none" stroke="#f5efe2" stroke-width="1.5"/><text y="8" text-anchor="middle" font-family="Italiana, serif" font-size="28" fill="#f5efe2">N</text></g>
</svg>`;

// ---------------------------------------------------------------------------
// 06 — Pixel Arcade (countdown)  ·  card 620×300  ·  no defs
// ---------------------------------------------------------------------------

const pixelFrame = `<svg viewBox="0 0 620 300" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
    <rect width="620" height="300" fill="#48cae4"/>
    <rect x="8" y="8" width="604" height="284" fill="#1d2a3a"/>
    <rect x="16" y="16" width="588" height="268" fill="#0a121c"/>
    <g fill="#ffb703"><rect width="24" height="24"/><rect x="596" width="24" height="24"/><rect y="276" width="24" height="24"/><rect x="596" y="276" width="24" height="24"/></g>
    <g transform="translate(40 -16)"><rect width="240" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="228" height="20" fill="#1d2a3a"/><text x="120" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703" letter-spacing="2">⏳ TIME LEFT</text></g>
    <!-- pixel hourglass -->
    <g transform="translate(80 130)" fill="#ffb703">
      <rect x="-26" y="-40" width="52" height="8"/><rect x="-26" y="36" width="52" height="8"/>
      <rect x="-20" y="-32" width="40" height="6"/><rect x="-14" y="-26" width="28" height="6"/><rect x="-8" y="-20" width="16" height="6"/><rect x="-4" y="-14" width="8" height="14" fill="#48cae4"/>
      <rect x="-8" y="0" width="16" height="6" fill="#ff3d4d"/><rect x="-14" y="6" width="28" height="6" fill="#ff3d4d"/><rect x="-20" y="12" width="40" height="20" fill="#ff3d4d"/>
    </g>
    <g font-family="Press Start 2P, monospace" fill="#fff">
      <text x="150" y="120" font-size="44" fill="#ffb703">{{TIME}}</text>
      <text x="150" y="160" font-size="12" fill="#48cae4">BACK SOON · PRESS START</text>
    </g>
    <!-- depleting blocks -->
    <g transform="translate(40 200)">
      <rect width="540" height="30" fill="#000" stroke="#48cae4" stroke-width="3"/>
      <g fill="#48cae4"><rect x="6" y="6" width="30" height="18"/><rect x="42" y="6" width="30" height="18"/><rect x="78" y="6" width="30" height="18"/><rect x="114" y="6" width="30" height="18"/><rect x="150" y="6" width="30" height="18"/><rect x="186" y="6" width="30" height="18"/><rect x="222" y="6" width="30" height="18"/></g>
    </g>
    <text x="40" y="270" font-family="Press Start 2P, monospace" font-size="10" fill="#ff3d4d">@ERIOL.STREAMS</text>
</svg>`;

// ---------------------------------------------------------------------------
// 07 — Cottagecore (clock)  ·  card 600×300
// defs: tco-linen, tco-st, tco-flower (referenced via href)
// ---------------------------------------------------------------------------

const cottageFrame = `<svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="cottage-tco-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="cottage-tco-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <g id="cottage-tco-flower"><circle r="9" fill="#ff9da8"/><circle cx="-9" cy="-4" r="7" fill="#ff9da8"/><circle cx="9" cy="-4" r="7" fill="#ff9da8"/><circle cx="-5" cy="7" r="7" fill="#ff9da8"/><circle cx="5" cy="7" r="7" fill="#ff9da8"/><circle r="4" fill="#ffd060"/></g>
  </defs>
    <rect width="600" height="300" fill="url(#cottage-tco-linen)" stroke="#6b4a2a" stroke-width="2"/>
    <rect width="600" height="24" fill="url(#cottage-tco-st)"/>
    <rect y="276" width="600" height="24" fill="url(#cottage-tco-st)"/>
    <rect width="24" height="300" fill="url(#cottage-tco-st)"/>
    <rect x="576" width="24" height="300" fill="url(#cottage-tco-st)"/>
    <text x="300" y="66" text-anchor="middle" font-family="DM Mono, monospace" font-size="13" fill="#b85a4a" letter-spacing="12">— el té se está infusionando —</text>
    <!-- embroidered hourglass -->
    <g transform="translate(120 178)">
      <!-- frame -->
      <g stroke="#6b4a2a" stroke-width="4" stroke-linecap="round" fill="none"><line x1="-44" y1="-66" x2="44" y2="-66"/><line x1="-44" y1="66" x2="44" y2="66"/></g>
      <!-- glass -->
      <path d="M-36 -60 L36 -60 L4 0 L36 60 L-36 60 L-4 0 Z" fill="#fff8e8" stroke="#6b4a2a" stroke-width="3"/>
      <!-- top sand -->
      <path d="M-30 -54 L30 -54 L4 -6 L-4 -6 Z" fill="#e0a32a"/>
      <!-- falling stream -->
      <line x1="0" y1="-6" x2="0" y2="30" stroke="#e0a32a" stroke-width="3"/>
      <!-- bottom pile -->
      <path d="M-30 54 L30 54 Q 18 30 0 30 Q -18 30 -30 54 Z" fill="#e0a32a"/>
      <!-- stitched dashes around -->
      <path d="M-44 -66 L44 -66" stroke="#b85a4a" stroke-width="1.5" stroke-dasharray="4 4"/>
    </g>
    <text x="230" y="170" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="68" fill="#2a1a08">{{TIME}}</text>
    <text x="230" y="208" font-family="Cormorant Garamond, serif" font-style="italic" font-size="22" fill="#5a7a4a">— preparando el mate y volvemos —</text>
    <use href="#cottage-tco-flower" transform="translate(250 232)"/>
    <text x="290" y="250" font-family="DM Mono, monospace" font-size="12" fill="#b85a4a" letter-spacing="8">@eriol.streams · 17:28</text>
</svg>`;

// ---------------------------------------------------------------------------
// 08 — Brutalist (countdown)  ·  card 600×300  ·  no defs
// ---------------------------------------------------------------------------

const brutalistFrame = `<svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
    <rect width="600" height="300" fill="#1a1a1a"/>
    <rect width="24" height="300" fill="#ff5a2a"/>
    <text x="44" y="50" font-family="DM Mono, monospace" font-size="14" fill="#ff5a2a" letter-spacing="14">// VOLVEMOS EN</text>
    <text x="44" y="186" font-family="Anton, sans-serif" font-size="150" fill="#f5f5f5" letter-spacing="-4">{{TIME}}</text>
    <rect x="44" y="212" width="512" height="24" fill="#0a0a0a" stroke="#c8c2b8" stroke-width="1"/>
    <rect x="44" y="212" width="360" height="24" fill="#ff5a2a"/>
    <text x="44" y="278" font-family="DM Mono, monospace" font-size="14" fill="#c8c2b8" letter-spacing="10">STAND BY · @ERIOL.STREAMS</text>
</svg>`;

// ---------------------------------------------------------------------------
// 09 — Anime VHS (countdown)  ·  card 606×300 (pink card + cyan ghost at x=6)
// defs: ta-rgb (filter on the group), ta-scan, ta-bar
// ---------------------------------------------------------------------------

const animeFrame = `<svg viewBox="0 0 606 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-ta-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="anime-ta-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".15"/></pattern>
    <linearGradient id="anime-ta-bar" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#ff7ad0"/><stop offset="100%" stop-color="#7ad0ff"/></linearGradient>
  </defs>
  <g filter="url(#anime-ta-rgb)">
    <rect x="6" y="0" width="600" height="300" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="600" height="300" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="580" height="280" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="600" height="300" fill="url(#anime-ta-scan)"/>
    <g><rect width="220" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">もうすぐ · BRB</text></g>
    <text x="300" y="170" text-anchor="middle" font-family="VT323, monospace" font-size="120" fill="#fff7c2" letter-spacing="4">{{TIME}}</text>
    <rect x="32" y="200" width="536" height="26" fill="#1a0a2a" stroke="#7ad0ff" stroke-width="2"/>
    <rect x="36" y="204" width="360" height="18" fill="url(#anime-ta-bar)"/>
    <text x="32" y="262" font-family="VT323, monospace" font-size="24" fill="#ff7ad0" letter-spacing="6">▮ STARTING SOON · またね !</text>
  </g>
</svg>`;

// ---------------------------------------------------------------------------
// 10 — Neon Tube / Vapor (countdown)  ·  card 600×300
// defs: nt-brick, nt-glow, nt-soft  ·  time is double-stroked glass tubing
// ---------------------------------------------------------------------------

const vaporFrame = `<svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="vapor-nt-brick" width="100" height="48" patternUnits="userSpaceOnUse">
      <rect width="100" height="48" fill="#16161e"/>
      <rect width="100" height="3" y="22" fill="#0a0a0f"/>
      <rect width="3" height="48" x="50" fill="#0a0a0f"/>
      <rect width="3" height="24" x="0" fill="#0a0a0f"/>
    </pattern>
    <filter id="vapor-nt-glow" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="6"/></filter>
    <filter id="vapor-nt-soft" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="16"/></filter>
  </defs>
    <rect width="600" height="300" rx="10" fill="url(#vapor-nt-brick)"/>
    <rect width="600" height="300" rx="10" fill="none" stroke="#0a0a0f" stroke-width="2"/>
    <!-- ambient neon wash on the wall -->
    <ellipse cx="300" cy="180" rx="260" ry="120" fill="#ff2e88" opacity=".14" filter="url(#vapor-nt-soft)"/>
    <!-- "BACK IN" cyan tube label -->
    <g filter="url(#vapor-nt-glow)"><text x="300" y="74" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="30" fill="#00f0ff" letter-spacing="14">BACK IN</text></g>
    <text x="300" y="74" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="30" fill="#cffaff" letter-spacing="14">BACK IN</text>
    <!-- mounting bracket dots -->
    <g fill="#0a0a0f" stroke="#2a2a33" stroke-width="1"><circle cx="60" cy="40" r="5"/><circle cx="540" cy="40" r="5"/></g>
    <!-- big neon time as glass tubing -->
    <g filter="url(#vapor-nt-glow)"><text x="300" y="210" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="120" fill="none" stroke="#ff2e88" stroke-width="9" letter-spacing="6">{{TIME}}</text></g>
    <text x="300" y="210" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="120" fill="none" stroke="#ffd6ec" stroke-width="2.5" letter-spacing="6">{{TIME}}</text>
    <!-- tube end caps glow -->
    <circle cx="116" cy="172" r="5" fill="#ff2e88" filter="url(#vapor-nt-glow)"/>
    <circle cx="484" cy="172" r="5" fill="#ff2e88" filter="url(#vapor-nt-glow)"/>
    <!-- yellow underline tube + buzz dot -->
    <g filter="url(#vapor-nt-glow)"><line x1="120" y1="246" x2="480" y2="246" stroke="#f5d019" stroke-width="5" stroke-linecap="round"/></g>
    <line x1="120" y1="246" x2="480" y2="246" stroke="#fff7d0" stroke-width="1.5" stroke-linecap="round"/>
    <text x="120" y="282" font-family="DM Mono, monospace" font-size="13" fill="#00f0ff" letter-spacing="8" opacity=".85">STARTING SOON</text>
    <g filter="url(#vapor-nt-glow)"><circle cx="492" cy="276" r="7" fill="#ff2e88"/></g>
    <text x="446" y="282" text-anchor="end" font-family="DM Mono, monospace" font-size="13" fill="#ff7ac8" letter-spacing="6">@eriol</text>
</svg>`;

// `TIME` (and the `flip` split-flap halves `MM`/`SS`) are SYSTEM-generated MM:SS
// strings, not user-controlled text — so they are NOT escaped/capped (matching
// the old literal `.replace(/\{\{TIME\}\}/g, time)` swap).
const TIME_SLOT = { kind: "time" } as const;

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const TIMER_DESIGNS: TimerDesign[] = [
  {
    id: "dark",
    name: "Dark Minimal",
    subtitle: "Reference style",
    widgetType: "timer",
    category: "countdown",
    description:
      'Dotted charcoal card, "VOLVEMOS EN" label and a big mono 05:00 with thin shrinking progress bar underneath.',
    swatches: ["#0d0d12", "#1a1a22", "#3ad96b", "#7fbfff"],
    cardWidth: 600,
    cardHeight: 300,
    fonts: ["Inter", "DM Mono"],
    frame: darkFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "flip",
    name: "Flip Clock",
    subtitle: "Split-flap board",
    widgetType: "timer",
    category: "countdown",
    description:
      'Two split-flap tiles for MM:SS with a center hinge line, soft top gloss and a small "STARTING SOON" caption.',
    swatches: ["#15151a", "#26262e", "#f5f5f5", "#ffb703"],
    cardWidth: 620,
    cardHeight: 300,
    fonts: ["DM Mono"],
    frame: flipFrame,
    slots: { MM: TIME_SLOT, SS: TIME_SLOT },
  },
  {
    id: "y2k",
    name: "Y2K Aqua Gel",
    subtitle: "Glossy ring",
    widgetType: "timer",
    category: "countdown",
    description:
      'Aqua gel slab with a glossy progress ring around the time and a green LED "LIVE IN" indicator.',
    swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
    cardWidth: 600,
    cardHeight: 300,
    fonts: ["Audiowide", "DM Mono"],
    frame: y2kFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "cyber",
    name: "Cyberpunk Neon",
    subtitle: "HUD T-minus",
    widgetType: "timer",
    category: "countdown",
    description:
      'Chamfered HUD with "T-MINUS" Orbitron clock, segmented depletion bar and a katakana system tag.',
    swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
    cardWidth: 640,
    cardHeight: 300,
    fonts: ["Orbitron", "DM Mono"],
    frame: cyberFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "editorial",
    name: "Editorial",
    subtitle: "Almanac clock",
    widgetType: "timer",
    category: "clock",
    description:
      'Cream card with a small Roman-numeral analog clock and an italic "vuelvo a las…" line beside the digital time.',
    swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
    cardWidth: 600,
    cardHeight: 300,
    fonts: ["Cormorant Garamond", "DM Mono", "Italiana"],
    frame: editorialFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "pixel",
    name: "Pixel Arcade",
    subtitle: "Bomb timer",
    widgetType: "timer",
    category: "countdown",
    description:
      "8-bit dialog box with Press Start 2P 05:00, a pixel hourglass sprite and a depleting blocky time bar.",
    swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
    cardWidth: 620,
    cardHeight: 300,
    fonts: ["Press Start 2P"],
    frame: pixelFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "cottage",
    name: "Cottagecore",
    subtitle: "Stitched timer",
    widgetType: "timer",
    category: "clock",
    description:
      'Linen card with cross-stitch border, an embroidered pocket-watch and an italic "vuelvo en un ratito" line.',
    swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
    cardWidth: 600,
    cardHeight: 300,
    fonts: ["Cormorant Garamond", "DM Mono"],
    frame: cottageFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "brutalist",
    name: "Brutalist",
    subtitle: "Stencil counter",
    widgetType: "timer",
    category: "countdown",
    description:
      'Black slab with orange bar and a huge Anton 05:00, hard-edged depletion track and mono "back in" caption.',
    swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
    cardWidth: 600,
    cardHeight: 300,
    fonts: ["Anton", "DM Mono"],
    frame: brutalistFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "anime",
    name: "Anime VHS",
    subtitle: "もうすぐ timer",
    widgetType: "timer",
    category: "countdown",
    description:
      'Pink double-stroke card with cyan tracking ghost, katakana "もうすぐ", RGB-split clock and VT323 caption.',
    swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
    cardWidth: 606,
    cardHeight: 300,
    fonts: ["VT323", "DM Mono"],
    frame: animeFrame,
    slots: { TIME: TIME_SLOT },
  },
  {
    id: "vapor",
    name: "Neon Tube",
    subtitle: "Glass sign countdown",
    widgetType: "timer",
    category: "countdown",
    description:
      'Dark brick wall with a glowing neon-tube "BACK IN" sign, the time rendered as bent glass tubing with bloom and a flickering buzz dot.',
    swatches: ["#0a0a12", "#ff2e88", "#00f0ff", "#f5d019"],
    cardWidth: 600,
    cardHeight: 300,
    fonts: ["Audiowide", "DM Mono"],
    frame: vaporFrame,
    slots: { TIME: TIME_SLOT },
  },
];

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

/**
 * Split a "MM:SS" (or arbitrary) time string into its minutes/seconds halves
 * around the first ":". Used to fill the `flip` design's split-flap tiles when
 * explicit mm/ss are not supplied.
 */
function deriveMmSs(time: string): { mm: string; ss: string } {
  const idx = time.indexOf(":");
  if (idx === -1) {
    return { mm: time, ss: "" };
  }
  return { mm: time.slice(0, idx), ss: time.slice(idx + 1) };
}

/**
 * Render a design's card SVG with the live time substituted in. `{{TIME}}` is
 * filled with `time`; `{{MM}}` / `{{SS}}` (the `flip` split-flap tiles) are
 * filled with `opts.mm` / `opts.ss`, falling back to the halves of `time`.
 *
 * Delegates to the generic, pure-data {@link renderDesign}, binding the values
 * to the design's `slots`. Returns a self-contained `<svg>` string (defs ids
 * are already design-prefixed in the catalog) suitable for inlining in the DOM
 * or encoding as an SVG data-URL for a Pixi texture.
 */
export function renderTimerDesignSvg(
  design: TimerDesign,
  time: string,
  opts?: { mm?: string; ss?: string },
): string {
  const derived = deriveMmSs(time);
  const mm = opts?.mm ?? derived.mm;
  const ss = opts?.ss ?? derived.ss;
  return renderDesign(design, { values: { TIME: time, MM: mm, SS: ss } });
}

/**
 * Look up a design by id. First-party catalog wins; if the id is not a shipped
 * design, fall back to the INSTALLED community-design registry (a design the
 * user applied from the marketplace), so `options.designId` pointing at a
 * backend id still resolves live in the Pixi widget + preview. Returns
 * `undefined` for an absent/unknown id.
 *
 * The fallback is a `SerializableDesign` constrained to `widgetType:"timer"`;
 * its `category` is a free string rather than the `TimerDesignCategory` union,
 * but no consumer reads `category` off this result — they use `cardWidth`,
 * `cardHeight`, `fonts` and pass it to `renderTimerDesignSvg` — so the cast is
 * safe.
 */
export function getTimerDesign(
  id: string | undefined,
): TimerDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = TIMER_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  return getInstalledDesign(id, "timer") as TimerDesign | undefined;
}
